import { useState, useEffect } from 'react'
import { usePagination, type PaginationProps } from '../../../hooks/use-pagination'
import { Column, ColumnSort, ColumnType, type RowData } from '../../../Components/Table/DataTable/DataTable.model'
import { FormatDateValue } from '../../../core/Utilities'
import { Box, Typography, Button } from '@mui/material'
import DataTable from '../../../Components/Table/DataTable'
import { type SupplementalNutritionModel, type postError } from '../../../core/types'
import { SupplementalNutritionAddEdit } from './SupplementalNutritionAddEdit'
import { sendGet, sendPost, sendPut } from '../../../hooks/use-fetch'
import { toast } from 'react-toastify'
import { usePermissions } from '../../../hooks/use-permissions'

export interface SupplementalNutritionListProps {
  childId: number
}

export function SupplementalNutritionList (props: SupplementalNutritionListProps): JSX.Element {
  const [loading, setLoading] = useState(true)
  const [refreshTime, setRefreshTime] = useState(new Date())
  const [sneList, setSNEList] = useState<RowData[]>([])
  const [errors, setErrors] = useState<string[] | undefined>(undefined)
  const [selectedRecord, setSelectedRecord] = useState<SupplementalNutritionModel | null>(null)
  const [open, setOpen] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const perms = usePermissions()

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, error, success } = await sendGet(`/SupplementalNutrition/GetList?childid=${props.childId}`, {})
      if (success && error[0] === '' && response !== null) {
        setSNEList(response)
      }
      setLoading(false)
    }
    void loadData()
  }, [refreshTime])

  const startDateColumn = new Column('startDate', 'Start Date', ColumnType.CUSTOM)
  startDateColumn.customCellGenerator = row => {
    return <div>{FormatDateValue(row.startDate)} </div>
  }
  const endDateColumn = new Column('endDate', 'End Date', ColumnType.CUSTOM)
  endDateColumn.customCellGenerator = row => {
    return <div>{FormatDateValue(row.endDate)} </div>
  }
  const eligibilityColumn = new Column('supplementalNutritionEligibility', 'Eligibility')
  const sourceColumn = new Column('supplementalNutritionSource', 'Source')

  const columns = [startDateColumn, endDateColumn, eligibilityColumn, sourceColumn]

  const paginationProps: PaginationProps<RowData> = {
    rows: sneList,
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  const addClick = (): void => {
    const initialRecord: SupplementalNutritionModel = {
      id: 0,
      childID: props.childId,
      endDate: null,
      startDate: new Date(),
      supplementalNutritionEligibilityID: 0,
      supplementalNutritionEligibility: '',
      supplementalNutritionSourceID: 0,
      supplementalNutritionSource: ''
    }
    setSelectedRecord(initialRecord)
    setOpen(true)
    setIsNew(true)
  }

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    const selected: SupplementalNutritionModel = {
      id: row.id,
      childID: props.childId,
      endDate: row.endDate,
      startDate: row.startDate,
      supplementalNutritionEligibilityID: row.supplementalNutritionEligibilityID,
      supplementalNutritionEligibility: row.supplementalNutritionEligibility,
      supplementalNutritionSourceID: row.supplementalNutritionSourceID,
      supplementalNutritionSource: row.supplementalNutritionSource
    }
    setSelectedRecord(selected)
    setOpen(true)
    setIsNew(false)
  }

  const handleSubmit = async (record: SupplementalNutritionModel): Promise<void> => {
    if (!loading) {
      setLoading(true)
      setErrors(undefined)
      let rsp: { errors: postError[], success: boolean, response: any }
      const url = `/SupplementalNutrition/${isNew ? 'Insert' : 'Update'}`

      if (isNew) {
        const { response } = await sendPost(url, record)
        rsp = response
      } else {
        const { response } = await sendPut(url, record)
        rsp = response
      }
      setLoading(false)

      if (rsp.success) {
        setOpen(false)
        setRefreshTime(new Date())
        toast.success('Changes have been saved')
      } else {
        const errorList: string[] = []
        rsp.errors?.forEach((e) => {
          errorList.push(e.error)
        })
        setErrors(errorList)
      }
    }
  }

  return <>
    <Typography variant="h6">Supplemental Nutrition Eligibility Periods</Typography>
    {perms.CareNetwork_SupplementalNutrition_Edit &&
      <Button variant='contained' color='secondary' className='mt-10' onClick={addClick} data-testid='add-period'>Add Period</Button>
    }
    <Box sx={{ width: '100%', padding: '30px 20px', borderRadius: '16px', background: '#fff' }}>
      <DataTable
        page={pagination.page}

        hasCheckbox={false}
        name='sneTable'
        columns={columns}
        rows={pagination.internalRows}
        totalRecords={pagination.recordCount}
        loading={loading}
        onSortChange={(col, sorts) => {
          pagination.onSortChange(col, sorts[0].order)
        }}
        onPageChange={pagination.handleChangePage}
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onRowClick={rowClick}
        rowClickable={perms.CareNetwork_SupplementalNutrition_Edit}
        initialColumnSorts={[new ColumnSort('startDate')]}
      />
    </Box>

    {open && selectedRecord !== null && (
      <SupplementalNutritionAddEdit
        onClose={() => { setOpen(false) }}
        onSubmit={handleSubmit}
        open={open}
        record={selectedRecord}
        errors={errors}
      />
    )}

  </>
}
